import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router";

/** Where Back goes when there's no earlier page in this visit to return to. */
const BACK_FALLBACK = "/songs";

/** History state for a song's detail page. */
export interface SongLocationState {
  song: Song;
}

/**
 * Opens a song's detail page as a new history entry. Works from any tab, and
 * Back returns to that tab. The song is stored in history state, so the page
 * doesn't have to look it up again, even after a reload.
 */
export function useOpenSong(): (song: Song) => void {
  const navigate = useNavigate();

  return useCallback(
    (song: Song) => {
      const state: SongLocationState = { song };

      void navigate(`/songs/${song.gid}`, { state });
    },
    [navigate]
  );
}

/**
 * Goes back to the previous screen, whichever tab it was on. When the app was
 * opened on this page (a new tab or a pasted link), the router's initial
 * location has the key "default". Going back from there would leave the site,
 * so Back goes to Discover instead.
 */
export function useGoBack(): () => void {
  const navigate = useNavigate();
  const { key } = useLocation();

  return useCallback(() => {
    if (key === "default") {
      void navigate(BACK_FALLBACK, { replace: true });
    } else {
      void navigate(-1);
    }
  }, [navigate, key]);
}
